import React, { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import { useProfile } from "../context/ProfileContext";
import Loader from "../components/Loader";
import SortableTh from "../components/SortableTh";
import NumberSharingNoteModal from "../components/NumberSharingNoteModal";
import NumberSharingCallerHistoryModal from "../components/NumberSharingCallerHistoryModal";
import NumberSharingInactiveModal from "../components/NumberSharingInactiveModal";
import RideDetailModal from "../components/RideDetailModal";

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSharedTimesDisplay = (_breakdown, total) => {
  if (total == null || total === "") return "—";
  return String(total);
};

const NumberSharingSuspendedCustomers = () => {
  const { profile, assignModule } = useProfile();
  const isNewType = profile?.employeeType === "new";
  const ridesModule = isNewType && assignModule?.rides?.enabled ? assignModule.rides : null;
  const canEditRides = !isNewType || (ridesModule && !!ridesModule.actions?.edit);

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 20,
    hasNext: false,
    hasPrev: false,
  });

  const [noteModal, setNoteModal] = useState({ show: false, customer: null });
  const [callerModal, setCallerModal] = useState({ show: false, customer: null });
  const [inactiveModal, setInactiveModal] = useState({
    show: false,
    customer: null,
    loading: false,
  });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [detailModal, setDetailModal] = useState({ show: false, ride: null });
  const [rideDetailLoading, setRideDetailLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfigRef = useRef({ key: null, direction: "asc" });

  const sortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hover:bg-gray-100";

  const fetchCustomers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      if (search.trim()) params.append("search", search.trim());
      if (sortConfigRef.current?.key) {
        params.append("sortBy", sortConfigRef.current.key);
        params.append("sortOrder", sortConfigRef.current.direction || "desc");
      }

      const response = await axiosInstance.get(
        `${API.ADMIN.NUMBER_SHARING_SUSPENDED_CUSTOMERS}?${params}`
      );

      if (response.data?.success) {
        setCustomers(response.data.data.customers || []);
        setPagination(response.data.data.pagination || pagination);
      }
    } catch (error) {
      console.error("Error fetching number sharing customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [search, pagination.limit]);

  useEffect(() => {
    fetchCustomers(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1);
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    sortConfigRef.current = next;
    fetchCustomers(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      sortConfigRef.current = next;
      fetchCustomers(1);
      return next;
    });
  };

  const sortTh = (columnKey, label, className = sortThClass) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={className}
      theme="green"
    />
  );

  const updateCustomerInList = (userId, patch) => {
    setCustomers((prev) =>
      prev.map((row) => (row.userId === userId ? { ...row, ...patch } : row))
    );
  };

  const handleNoteSaved = (userId, notes) => {
    updateCustomerInList(userId, { numberSharingNotes: notes });
  };

  const openRideDetail = async (rideId) => {
    if (!rideId) return;
    setRideDetailLoading(true);
    try {
      const response = await axiosInstance.get(`${API.ADMIN.RIDE_DETAIL}/${rideId}`);
      if (response.data?.success) {
        setDetailModal({
          show: true,
          ride: response.data.data.ride,
        });
      } else {
        toast.error("Ride not found");
      }
    } catch (error) {
      console.error("Error fetching ride details:", error);
      toast.error("Failed to load ride details");
    } finally {
      setRideDetailLoading(false);
    }
  };

  const handleStatusChange = async (customer, newStatus) => {
    if (!canEditRides) return;

    if (newStatus === "inactive") {
      setInactiveModal({ show: true, customer, loading: false });
      return;
    }

    setStatusUpdatingId(customer.userId);
    try {
      const response = await axiosInstance.patch(
        API.ADMIN.USER_ACCOUNT_STATUS(customer.userId),
        { accountStatus: "active" }
      );
      if (response.data?.success) {
        toast.success("Status updated to Active");
        updateCustomerInList(customer.userId, {
          accountStatus: "active",
          isDeactivated: false,
          status: "active",
          inactiveReason: null,
        });
      } else {
        toast.error(response.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleInactiveConfirm = async (reason) => {
    const customer = inactiveModal.customer;
    if (!customer) return;

    setInactiveModal((prev) => ({ ...prev, loading: true }));
    try {
      const response = await axiosInstance.patch(
        API.ADMIN.USER_ACCOUNT_STATUS(customer.userId),
        {
          accountStatus: "deactivated",
          suspensionReason: reason.trim(),
        }
      );
      if (response.data?.success) {
        toast.success("Status updated to Inactive");
        updateCustomerInList(customer.userId, {
          accountStatus: "deactivated",
          isDeactivated: true,
          status: "inactive",
          inactiveReason: reason.trim(),
        });
        setInactiveModal({ show: false, customer: null, loading: false });
      } else {
        toast.error(response.data?.message || "Failed to update status");
        setInactiveModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Inactive status error:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
      setInactiveModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Number Sharing Suspended — Customer
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Customers with rides where mobile number was shared (Shared with = Customer).
        </p>
      </div>

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by name or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 min-w-[240px] flex-1 max-w-md"
        />
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Search
        </button>
      </form>

      {loading && <Loader />}

      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {sortTh("customerName", "Customer name")}
                  {sortTh("mobileNumber", "Mobile number")}
                  {sortTh(
                    "latestSharedAt",
                    "Number shared date and time and comments",
                    `${sortThClass} min-w-[220px]`
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Caller ID
                  </th>
                  {sortTh("sharedTimesTotal", "Shared Times")}
                  {sortTh("status", "Status")}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No customers found.
                    </td>
                  </tr>
                ) : (
                  customers.map((row) => (
                    <tr key={row.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {row.customerName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {row.mobileNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {row.numberSharingNotes?.length ? (
                          <ul className="space-y-1 mb-2">
                            {row.numberSharingNotes.map((note, idx) => (
                              <li key={`${row.userId}-note-${idx}`} className="text-xs">
                                <span className="font-medium">
                                  {formatDateTime(note.sharedAt)}
                                </span>
                                {note.comments ? (
                                  <span className="text-gray-600"> — {note.comments}</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                        {canEditRides && (
                          <>
                            {" "}
                            <button
                              type="button"
                              onClick={() => setNoteModal({ show: true, customer: row })}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              Add
                            </button>
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <button
                          type="button"
                          onClick={() => setCallerModal({ show: true, customer: row })}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          View
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {formatSharedTimesDisplay(
                          row.sharedTimesBreakdown,
                          row.sharedTimesTotal
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm min-w-[8rem]">
                        <select
                          value={row.status || "active"}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          disabled={
                            !canEditRides || statusUpdatingId === row.userId
                          }
                          className="text-sm border border-gray-300 rounded px-2 py-1 capitalize focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        {row.status === "inactive" && row.inactiveReason && (
                          <p
                            className="text-xs text-gray-500 mt-1 max-w-[12rem] truncate"
                            title={row.inactiveReason}
                          >
                            {row.inactiveReason}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total}{" "}
                total)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!pagination.hasPrev}
                  onClick={() => fetchCustomers(pagination.currentPage - 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={!pagination.hasNext}
                  onClick={() => fetchCustomers(pagination.currentPage + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <NumberSharingNoteModal
        show={noteModal.show}
        customer={noteModal.customer}
        notesUrl={
          noteModal.customer
            ? API.ADMIN.NUMBER_SHARING_SUSPENDED_CUSTOMER_NOTES(noteModal.customer.userId)
            : null
        }
        onClose={() => setNoteModal({ show: false, customer: null })}
        onSaved={handleNoteSaved}
      />

      {rideDetailLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30">
          <Loader />
        </div>
      )}

      <NumberSharingCallerHistoryModal
        show={callerModal.show}
        sharedParty="Customer"
        customer={callerModal.customer}
        historyUrl={
          callerModal.customer
            ? API.ADMIN.NUMBER_SHARING_SUSPENDED_CUSTOMER_CALLER_HISTORY(
                callerModal.customer.userId
              )
            : null
        }
        onClose={() => setCallerModal({ show: false, customer: null })}
        onRideClick={openRideDetail}
      />

      <RideDetailModal
        show={detailModal.show}
        ride={detailModal.ride}
        onClose={() => setDetailModal({ show: false, ride: null })}
      />

      <NumberSharingInactiveModal
        show={inactiveModal.show}
        customer={inactiveModal.customer}
        loading={inactiveModal.loading}
        onClose={() => setInactiveModal({ show: false, customer: null, loading: false })}
        onConfirm={handleInactiveConfirm}
      />
    </div>
  );
};

export default NumberSharingSuspendedCustomers;
